/** BARELE CITIRILOR CARE MERG ÎN FUNDAL, pe pagina colecției.
 *
 *  DE CE AICI ȘI NU ÎN FEREASTRA DE PORNIRE. Fereastra aștepta importul să se
 *  termine — minute în care nu se putea face nimic altceva, nici măcar
 *  reîmprospăta pagina. Cerut 13.10.2026: «the upload of the tronson from an
 *  image should happen in the background... we should see a progress bar on the
 *  Tronsoane page, and we should be able to refresh the page, do other things,
 *  maybe upload other tronsoane».
 *
 *  LISTA VINE DE LA SERVER, nu din memoria ecranului — altfel o reîmprospătare
 *  ar fi șters barele, iar citirile ar fi mers mai departe nevăzute. De-aici
 *  vine și «mai multe deodată»: nimic nu e legat de fereastra care le-a pornit.
 *
 *  ȘI CEASUL E AL CITIRII, nu al paginii. Bara se mișcă pe secundele de când a
 *  PORNIT citirea (`pornit_la`, de la server). Socotită de când a văzut-o
 *  pagina întâi, ea sărea înapoi la zero la fiecare reîmprospătare și la
 *  fiecare întoarcere din editor — și atunci părea că importul a luat-o de la
 *  capăt: «if I go in a tronson and come back or refresh the page the upload
 *  starts again which is dumb» (13.10.2026). Importul nu se reluase niciodată;
 *  numai bara mințea.
 *
 *  FIECARE RÂND SE POATE OPRI, iar cel căzut se poate relua. Sunt cele două
 *  lucruri pe care omul le vrea de la o citire care nu merge, și până acum
 *  n-avea niciunul: scria «încearcă din nou» și nu era pe ce apăsa.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import * as api from "../../api";
import { useT } from "../../i18n";
import type { Cheie } from "../../i18n";
import { pasulCitirii, procentCitire } from "./calc";

/** La cât se întreabă serverul. Trei secunde, ca în fereastra de dinainte: o
 *  citire ține minute, deci mai des n-ar arăta nimic nou și ar ține degeaba
 *  un fir de lucru pe server pentru fiecare import. */
const BATAIA_MS = 3000;

/** Cât ține o bară terminată pe ecran înainte să plece. Destul cât omul să
 *  vadă CARE s-a terminat, nu atât cât să-i stea în drum. Cele CĂZUTE nu pleacă
 *  singure: ele cer o hotărâre — reluate sau date la o parte. */
const RAMANE_MS = 8000;

type Pornit = { cod: string; la: number; eroare?: string };

export default function ImporturiInLucru({ projectId, pornite, laTerminat,
                                           laOprit }: {
  /** Gol = colecția omului, ca peste tot (migrarea 0045). */
  projectId: string;
  /** Codurile pornite în sesiunea asta, ca bara să apară ÎNAINTE de prima
   *  întoarcere a serverului — fereastra se închide pe loc, iar urcarea unei
   *  planșe de câțiva megaocteți ține câteva secunde bune. */
  pornite: Pornit[];
  /** Un import a ajuns modul. Pagina își reîncarcă colecția. */
  laTerminat: (cod: string) => void;
  /** Un import a fost oprit și șters: pagina îl scoate din `pornite`. */
  laOprit: (cod: string) => void;
}) {
  const t = useT();
  const [lista, setLista] = useState<api.ImportImagine[]>([]);
  const [acum, setAcum] = useState(() => Date.now());
  const [lucrez, setLucrez] = useState<string | null>(null);
  /** Când a fost văzut întâi fiecare import — numai ca să se știe cât mai
   *  stă pe ecran unul TERMINAT. Bara se mișcă pe `pornit_la`. */
  const vazut = useRef(new Map<string, number>());
  const anuntate = useRef(new Set<string>());

  const bate = useCallback(async () => {
    try {
      const r = await (projectId ? api.importuriInLucru(projectId)
                                 : api.importurileMeleInLucru());
      for (const im of r.imports) {
        if (!vazut.current.has(im.id)) vazut.current.set(im.id, Date.now());
        if (im.stare === "scris" && !anuntate.current.has(im.id)) {
          anuntate.current.add(im.id);
          laTerminat(im.code);
        }
      }
      setLista(r.imports);
    } catch {
      // O bătaie pierdută nu e o eroare de arătat: următoarea o ia de la capăt.
    }
  }, [projectId, laTerminat]);

  useEffect(() => {
    let viu = true;
    const ceas = setInterval(() => { if (viu) void bate(); }, BATAIA_MS);
    void bate();
    return () => { viu = false; clearInterval(ceas); };
  }, [bate]);

  useEffect(() => {
    if (!lista.length && !pornite.length) return;
    const ceas = setInterval(() => setAcum(Date.now()), 500);
    return () => clearInterval(ceas);
  }, [lista.length, pornite.length]);

  const opreste = async (im: api.ImportImagine) => {
    setLucrez(im.id);
    try {
      await (projectId ? api.opresteImport(projectId, im.id)
                       : api.opresteImportulMeu(im.id));
      setLista((l) => l.filter((x) => x.id !== im.id));
      laOprit(im.code);
    } catch { /* rămâne pe ecran; următoarea bătaie spune adevărul */ }
    finally { setLucrez(null); }
  };

  const reia = async (im: api.ImportImagine) => {
    setLucrez(im.id);
    try {
      await (projectId ? api.reiaImport(projectId, im.id)
                       : api.reiaImportulMeu(im.id));
      vazut.current.delete(im.id);
      anuntate.current.delete(im.id);
      await bate();
    } catch { /* la fel */ }
    finally { setLucrez(null); }
  };

  const codurile = new Set(lista.map((im) => im.code));
  const inca = pornite.filter((p) => !codurile.has(p.cod)
    && (p.eroare || acum - p.la < 30000));
  const deAratat = lista.filter((im) => im.stare !== "scris"
    || acum - (vazut.current.get(im.id) ?? acum) < RAMANE_MS);

  if (!deAratat.length && !inca.length) return null;

  /** Un rând de bară. `de_cand` e în milisecunde, pe ceasul citirii. */
  const rand = (cheie: string, cod: string, de_cand: number,
                stare?: string, etapa?: api.ImportImagine["etapa"],
                mesaj?: string, im?: api.ImportImagine) => {
    const esuat = stare === "esuat";
    const gata = stare === "scris";
    const p = esuat || gata ? 100
      : Math.round(procentCitire((acum - de_cand) / 1000, stare));
    const pas = gata ? t("import.gata")
      : esuat ? (mesaj ?? t("import.esuat"))
        : (pasulCitirii(t, etapa) || t("import.pornit"));
    const ocupat = !!im && lucrez === im.id;
    return (
      <li key={cheie} className={`ob-import-rand${esuat ? " e-esuat" : ""}`}>
        <div className="ob-import-cap">
          <span className="ob-import-cod">{cod}</span>
          <span className="ob-import-pas">{pas}</span>
          {!esuat && !gata && <span className="ob-import-procent">{p}%</span>}
          {im && esuat && (
            <button type="button" className="ob-import-buton" disabled={ocupat}
                    onClick={() => { void reia(im); }}>
              {t("import.reia")}
            </button>
          )}
          {im && !gata && (
            <button type="button" className="ob-import-x" disabled={ocupat}
                    title={esuat ? t("import.inchide") : t("import.renunta")}
                    aria-label={esuat ? t("import.inchide") : t("import.renunta")}
                    onClick={() => { void opreste(im); }}>
              ×
            </button>
          )}
        </div>
        <div className="ob-import-sina" role="progressbar" aria-valuemin={0}
             aria-valuemax={100} aria-valuenow={p} aria-label={cod}>
          <div className={`ob-import-plin${gata ? " e-gata" : ""}`}
               style={{ width: `${p}%` }} />
        </div>
      </li>
    );
  };

  return (
    <section className="ob-importuri" aria-live="polite">
      <h3 className="ob-importuri-titlu">{t("import.titlu")}</h3>
      <ul className="ob-importuri-lista">
        {inca.map((p) => rand(`nou:${p.cod}`, p.cod, p.la,
                              p.eroare ? "esuat" : undefined, undefined, p.eroare))}
        {deAratat.map((im) => rand(
          im.id, im.code,
          im.pornit_la ? Date.parse(im.pornit_la)
                       : (vazut.current.get(im.id) ?? acum),
          im.stare, im.etapa,
          im.error ? t(`nou.import_${im.error}` as Cheie) : im.message, im))}
      </ul>
    </section>
  );
}
